import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { CSVLink } from 'react-csv';

const csvHeaders = [
  { label: 'Invoice No', key: 'invoice_no' },
  { label: 'Product Name', key: 'product_name' },
  { label: 'Quantity', key: 'qty' },
  { label: 'Cashier', key: 'cashier' },
  { label: 'Gross Total', key: 'gross_total' },
  { label: 'Net Total', key: 'net_total' },
  { label: 'Discount', key: 'discount' },
  { label: 'Bill Date/Time', key: 'bill_date_time' },
];

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value ?? '-';
  }
  return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    start: formatLocalDate(start),
    end: formatLocalDate(today),
  };
};

const AllSalesReport = () => {
  const monthBounds = useMemo(() => getMonthBounds(), []);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromDate, setFromDate] = useState(monthBounds.start);
  const [toDate, setToDate] = useState(monthBounds.end);
  const [productQuery, setProductQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const fetchSales = async () => {
      setLoading(true);
      setError('');
      try {
        const [detailResponse, headerResponse] = await Promise.all([
          axios.get('https://serverukautotech.ccsoftwares.com/sales/getAllBills'),
          axios.get('https://serverukautotech.ccsoftwares.com/billing/getBillHeaders'),
        ]);

        const headerMap = new Map(
          (headerResponse.data || []).map((header) => [header.inv_no, header])
        );

        const combined = (detailResponse.data || []).map((detail) => {
          const header = headerMap.get(detail.inv_no) || {};
          const billTimestamp = detail.creation_date || header.inv_date;
          const billDateObj = billTimestamp ? new Date(billTimestamp) : null;

          return {
            invoice_no: detail.inv_no,
            product_name: detail.item_name,
            qty: detail.qty,
            cashier: detail.username || header.username || '-',
            gross_total: header.gross_total ?? detail.total ?? 0,
            net_total: header.net_total ?? detail.total ?? 0,
            discount: header.discount ?? 0,
            bill_date_time: billDateObj ? billDateObj.toLocaleString() : 'N/A',
            billDateObj,
          };
        });

        combined.sort((a, b) => {
          if (a.billDateObj && b.billDateObj) {
            return b.billDateObj - a.billDateObj;
          }
          if (a.billDateObj) return -1;
          if (b.billDateObj) return 1;
          return 0;
        });

        setSales(combined);
      } catch (err) {
        console.error('Failed to load sales data', err);
        setError('Unable to load sales data at the moment.');
      } finally {
        setLoading(false);
      }
    };

    fetchSales();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [fromDate, toDate, productQuery]);

  const filteredSales = useMemo(() => {
    if (!sales.length) return [];

    const normalizedQuery = productQuery.trim().toLowerCase();
    const from = fromDate ? new Date(fromDate) : null;
    if (from) {
      from.setHours(0, 0, 0, 0);
    }
    const to = toDate ? new Date(toDate) : null;
    if (to) {
      to.setHours(23, 59, 59, 999);
    }

    return sales.filter((row) => {
      const matchesProduct = normalizedQuery
        ? row.product_name?.toLowerCase().includes(normalizedQuery)
        : true;

      if (!matchesProduct) {
        return false;
      }

      if (!from && !to) {
        return true;
      }

      if (!row.billDateObj) {
        return false;
      }

      const afterFrom = from ? row.billDateObj >= from : true;
      const beforeTo = to ? row.billDateObj <= to : true;

      return afterFrom && beforeTo;
    });
  }, [sales, fromDate, toDate, productQuery]);

  const todaysNetSales = useMemo(() => {
    if (!sales.length) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return sales.reduce((accumulator, row) => {
      if (!row.billDateObj) return accumulator;
      const rowDate = new Date(row.billDateObj);
      rowDate.setHours(0, 0, 0, 0);
      if (rowDate.getTime() === today.getTime()) {
        return accumulator + Number(row.net_total || 0);
      }
      return accumulator;
    }, 0);
  }, [sales]);

  const selectedPeriodNetSales = useMemo(
    () =>
      filteredSales.reduce(
        (accumulator, row) => accumulator + Number(row.net_total || 0),
        0
      ),
    [filteredSales]
  );

  const selectedPeriodDiscount = useMemo(
    () =>
      filteredSales.reduce(
        (accumulator, row) => accumulator + Number(row.discount || 0),
        0
      ),
    [filteredSales]
  );

  const csvData = useMemo(
    () =>
      filteredSales.map((row) => ({
        invoice_no: row.invoice_no,
        product_name: row.product_name,
        qty: row.qty,
        cashier: row.cashier,
        gross_total: row.gross_total,
        net_total: row.net_total,
        discount: row.discount,
        bill_date_time: row.bill_date_time,
      })),
    [filteredSales]
  );

  const paginatedSales = useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredSales;
    }
    const startIndex = page * rowsPerPage;
    return filteredSales.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredSales, page, rowsPerPage]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            All Sales Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review billed line items with flexible date and product filters.
          </Typography>
        </Box>
        <CSVLink data={csvData} headers={csvHeaders} filename="all-sales-report.csv" style={{ textDecoration: 'none' }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
            disabled={!filteredSales.length}
          >
            Download CSV
          </Button>
        </CSVLink>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Today&apos;s Net Sales
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(todaysNetSales)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Selected Period Net Sales
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(selectedPeriodNetSales)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Selected Period Discounts
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(selectedPeriodDiscount)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Search by Product"
              placeholder="Start typing product name"
              fullWidth
              value={productQuery}
              onChange={(event) => setProductQuery(event.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#000000' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Invoice No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Product Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Qty</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Cashier</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Gross Total (Rs.)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Net Total (Rs.)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Discount (Rs.)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Bill Date / Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No sales records match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSales.map((row, index) => (
                  <TableRow key={`${row.invoice_no}-${row.product_name}-${index}`}>
                    <TableCell>{row.invoice_no}</TableCell>
                    <TableCell>{row.product_name}</TableCell>
                    <TableCell>{row.qty}</TableCell>
                    <TableCell>{row.cashier}</TableCell>
                    <TableCell>{formatCurrency(row.gross_total)}</TableCell>
                    <TableCell>{formatCurrency(row.net_total)}</TableCell>
                    <TableCell>{formatCurrency(row.discount)}</TableCell>
                    <TableCell>{row.bill_date_time}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!loading && !error && (
        <TablePagination
          component="div"
          count={filteredSales.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
          sx={{ mt: 2 }}
        />
      )}
    </Box>
  );
};

export default AllSalesReport;
